import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GraduationCap, CheckCircle2, AlertTriangle, Loader2, Save } from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";

/**
 * Notas dos formandos — atribui/corrige nota final (escala PT 0-20).
 *
 * Só lista formandos com documentação completa (Ficha + Contrato + Menor se
 * aplicável). Formandos incompletos aparecem numa secção separada para o
 * admin saber quem tem docs em falta.
 */
export default function AdminCursoNotas() {
  const [params] = useSearchParams();
  const cursoId = params.get("curso") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState({}); // { fichaId: string }
  const [dimDrafts, setDimDrafts] = useState({}); // { fichaId: {Ava_X: value|"" } }
  const [obsGlobalDraft, setObsGlobalDraft] = useState("");
  const [obsGlobalSaving, setObsGlobalSaving] = useState(false);
  const [saving, setSaving] = useState({}); // { fichaId: bool }

  const load = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true); setError("");
    try {
      const r = await adminApi.cursoNotasList(cursoId);
      setData(r);
      // Inicializa drafts com nota actual formatada
      const seed = {};
      (r.elegiveis || []).forEach((it) => {
        seed[it.ID_FichaInscricao] = it.Nota != null ? String(it.Nota) : "";
      });
      setDrafts(seed);
      setObsGlobalDraft(r.observacoes_global || "");
    } catch (e) {
      setError(e.message || "Erro a carregar notas");
    } finally {
      setLoading(false);
    }
  }, [cursoId]);

  useEffect(() => { load(); }, [load]);

  const saveNota = async (fichaId) => {
    setSaving((s) => ({ ...s, [fichaId]: true }));
    try {
      const draft = drafts[fichaId];
      const currentStr = data?.elegiveis?.find((f) => f.ID_FichaInscricao === fichaId)?.Nota;
      const currentAsStr = currentStr != null ? String(currentStr) : "";
      const draftNorm = (draft ?? "").trim().replace(",", ".");
      const notaChanged = draftNorm !== currentAsStr;

      // 1) Nota — só grava se draft mudou
      if (notaChanged) {
        if (draftNorm === "") {
          await adminApi.cursoNotaSet(cursoId, fichaId, null);
        } else {
          const v = parseFloat(draftNorm);
          if (isNaN(v) || v < 0 || v > 20) {
            toast.error("Nota tem de ser um número entre 0 e 20");
            setSaving((s) => ({ ...s, [fichaId]: false }));
            return;
          }
          await adminApi.cursoNotaSet(cursoId, fichaId, v);
        }
      }
      // 2) Dimensões
      const dimPatch = dimDrafts[fichaId];
      const patch = {};
      if (dimPatch) {
        for (const [k, v] of Object.entries(dimPatch)) {
          patch[k] = v === "" ? null : Number(v);
        }
      }
      if (Object.keys(patch).length > 0) {
        await adminApi.cursoAvaliacaoSet(cursoId, fichaId, patch);
      }
      toast.success("Guardado");
      setDimDrafts((s) => { const c = { ...s }; delete c[fichaId]; return c; });
      await load();
    } catch (e) {
      toast.error(e.message || "Erro a guardar");
    } finally { setSaving((s) => ({ ...s, [fichaId]: false })); }
  };

  const saveObsGlobal = async () => {
    setObsGlobalSaving(true);
    try {
      await adminApi.cursoAvaliacaoObsGlobalSet(cursoId, obsGlobalDraft || "");
      toast.success("Observações globais guardadas");
      await load();
    } catch (e) {
      toast.error(e.message || "Erro a guardar observações");
    } finally { setObsGlobalSaving(false); }
  };

  const elegiveis = data?.elegiveis || [];
  const incompletas = data?.incompletas || [];
  const comNota = useMemo(() => elegiveis.filter((it) => it.Nota != null).length, [elegiveis]);

  if (!cursoId) {
    return (
      <div className="p-6 text-[#6E6754]">
        Falta o parâmetro <code>?curso=&lt;ID&gt;</code> no URL.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-curso-notas-page">
      <CursoTabsNav cursoId={cursoId} current="notas" />

      <header className="mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3F0E7] grid place-items-center">
          <GraduationCap className="w-5 h-5 text-[#A8843C]" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[#1B1B1B]" data-testid="notas-page-title">
            Notas do curso #{cursoId}
          </h1>
          <p className="text-sm text-[#6E6754]">
            Atribui/corrige a nota final (0-20) dos formandos com documentação completa.
          </p>
        </div>
      </header>

      {loading && (
        <div className="flex items-center gap-2 py-6 text-[#6E6754]">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
        </div>
      )}

      {error && !loading && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5" data-testid="notas-stats">
            <div className="bg-white border border-[#E6E1D6] rounded-md p-3">
              <div className="text-xs text-[#978D78] uppercase tracking-wider">Elegíveis</div>
              <div className="text-2xl font-semibold text-[#1B1B1B]">{elegiveis.length}</div>
            </div>
            <div className="bg-white border border-[#E6E1D6] rounded-md p-3">
              <div className="text-xs text-[#978D78] uppercase tracking-wider">Com nota</div>
              <div className="text-2xl font-semibold text-[#A8843C]">{comNota}</div>
            </div>
            <div className="bg-white border border-[#E6E1D6] rounded-md p-3">
              <div className="text-xs text-[#978D78] uppercase tracking-wider">Docs incompletos</div>
              <div className="text-2xl font-semibold text-orange-600">{incompletas.length}</div>
            </div>
          </div>

          <section className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden mb-6">
            <header className="px-4 py-2 border-b border-[#F3F0E7] flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="font-semibold text-[#1B1B1B]">Formandos elegíveis</span>
              <span className="text-[#978D78]">({elegiveis.length})</span>
            </header>
            {elegiveis.length === 0 ? (
              <div className="p-4 text-sm text-[#6E6754] italic">
                Nenhum formando com documentação completa ainda.
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF8F2] text-left text-[10px] uppercase tracking-wider text-[#978D78]">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2 min-w-[180px]">Nome</th>
                    {_DIM_LABELS.map(([k, lbl]) => (
                      <th key={k} className="px-1 py-2 text-center min-w-[54px]" title={_DIM_TITLES[k]}>{lbl}</th>
                    ))}
                    <th className="px-2 py-2 text-center bg-[#FAF3E4] min-w-[52px]">Média</th>
                    <th className="px-3 py-2 w-32">Nota (0-20)</th>
                    <th className="px-3 py-2 w-24 sticky right-0 bg-[#FAF8F2] shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]"></th>
                  </tr>
                </thead>
                <tbody>
                  {elegiveis.map((it) => {
                    const draft = drafts[it.ID_FichaInscricao] ?? "";
                    const currentStr = it.Nota != null ? String(it.Nota) : "";
                    const notaDirty = draft.trim().replace(",", ".") !== currentStr;
                    const dimDirty = !!dimDrafts[it.ID_FichaInscricao];
                    const dirty = notaDirty || dimDirty;
                    return (
                      <tr key={it.ID_FichaInscricao} className="border-t border-[#F3F0E7] group hover:bg-[#FCFAF3]"
                          data-testid={`notas-row-${it.ID_FichaInscricao}`}>
                        <td className="px-3 py-2 text-[#978D78]">{it.ID_FichaInscricao}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-[#1B1B1B]">{it.NomeCompleto}</div>
                          <div className="text-xs text-[#978D78]">{it.Email}</div>
                          {it.Repetente && (
                            <span className="inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                              Repetente
                            </span>
                          )}
                        </td>
                        {_DIM_LABELS.map(([k]) => {
                          const draftVal = dimDrafts[it.ID_FichaInscricao]?.[k];
                          const current = draftVal !== undefined ? draftVal : (it[k] ?? "");
                          return (
                            <td key={k} className="px-1 py-1 text-center">
                              <select
                                className="w-11 border border-[#E6E1D6] rounded px-1 py-0.5 text-xs focus:border-[#A8843C] focus:outline-none"
                                value={current}
                                onChange={(e) => setDimDrafts((s) => ({
                                  ...s,
                                  [it.ID_FichaInscricao]: {
                                    ...(s[it.ID_FichaInscricao] || {}),
                                    [k]: e.target.value,
                                  },
                                }))}
                                data-testid={`ava-${it.ID_FichaInscricao}-${k}`}
                              >
                                <option value="">—</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                              </select>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-center font-bold bg-[#FAF3E4]">
                          {_mediaDims(it) ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="w-24 border border-[#E6E1D6] rounded px-2 py-1 text-sm focus:border-[#A8843C] focus:outline-none"
                            placeholder="—"
                            value={draft}
                            onChange={(e) => setDrafts((s) => ({ ...s, [it.ID_FichaInscricao]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") saveNota(it.ID_FichaInscricao); }}
                            data-testid={`nota-input-${it.ID_FichaInscricao}`}
                            disabled={saving[it.ID_FichaInscricao]}
                          />
                        </td>
                        <td className="px-3 py-2 sticky right-0 bg-white group-hover:bg-[#FCFAF3] shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]">
                          <button
                            onClick={() => saveNota(it.ID_FichaInscricao)}
                            disabled={saving[it.ID_FichaInscricao]}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded transition ${
                              dirty
                                ? "bg-[#A8843C] text-white hover:bg-[#8f6e2f]"
                                : "bg-[#E6E1D6] text-[#6E6754] hover:bg-[#D6CFBE]"
                            } disabled:opacity-50`}
                            data-testid={`nota-save-${it.ID_FichaInscricao}`}
                          >
                            {saving[it.ID_FichaInscricao] ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Save className="w-3 h-3" />
                            )}
                            Gravar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </section>

          {/* ── Observações globais do Quadro (Mod. 17) ─────────────── */}
          <section className="bg-white border border-[#E6E1D6] rounded-md p-4 mb-6" data-testid="notas-obs-global-section">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-sm font-semibold text-[#1B1B1B]">Observações do Quadro (globais)</h2>
                <p className="text-xs text-[#978D78]">
                  Notas gerais aplicáveis à turma inteira. Impressas no rodapé do Mod. 17.
                </p>
              </div>
              <button
                onClick={saveObsGlobal}
                disabled={obsGlobalSaving}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-[#A8843C] text-white hover:bg-[#8f6e2f] disabled:opacity-50"
                data-testid="notas-obs-global-save"
              >
                {obsGlobalSaving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                Gravar
              </button>
            </div>
            <textarea
              value={obsGlobalDraft}
              onChange={(e) => setObsGlobalDraft(e.target.value)}
              placeholder="Ex: A turma apresentou bom desempenho global. Assiduidade acima da média…"
              rows={3}
              className="w-full border border-[#E6E1D6] rounded px-3 py-2 text-sm focus:border-[#A8843C] focus:outline-none resize-y"
              data-testid="notas-obs-global-input"
            />
          </section>

          {incompletas.length > 0 && (
            <section className="bg-white border border-orange-200 rounded-md overflow-x-auto">
              <header className="px-4 py-2 border-b border-orange-100 bg-orange-50 flex items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="font-semibold text-orange-900">
                  Formandos com documentação em falta
                </span>
                <span className="text-orange-700">({incompletas.length})</span>
                <span className="ml-auto text-xs text-orange-700 italic">
                  Sem nota atribuível até completarem os documentos.
                </span>
              </header>
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-orange-50/50 text-left text-[10px] uppercase tracking-wider text-orange-700">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Nome</th>
                    <th className="px-3 py-2">Em falta</th>
                  </tr>
                </thead>
                <tbody>
                  {incompletas.map((it) => (
                    <tr key={it.ID_FichaInscricao} className="border-t border-orange-100">
                      <td className="px-3 py-2 text-orange-800">{it.ID_FichaInscricao}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-[#1B1B1B]">{it.NomeCompleto}</div>
                        <div className="text-xs text-[#978D78]">{it.Email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {it.docs_missing.map((d) => (
                            <span key={d} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                              {d}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* ── Avaliação de Desempenho (Mod. 17) — bloco separado removido: as dimensões estão agora na tabela principal como colunas. ─────────────── */}
        </>
      )}
    </div>
  );
}


const _DIM_LABELS = [
  ["Ava_Dominio", "Domínio"],
  ["Ava_Generalizacao", "Generalização"],
  ["Ava_Participacao", "Participação"],
  ["Ava_Responsabilidades", "Responsabilidades"],
  ["Ava_RelacoesInterpessoais", "Relações Interpessoais"],
  ["Ava_AssiduidadePontualidade", "Assiduidade / Pontualidade"],
  ["Ava_Testes", "Testes"],
];

const _DIM_TITLES = {
  Ava_Dominio: "Domínio dos assuntos",
  Ava_Generalizacao: "Generalização dos saberes",
  Ava_Participacao: "Participação",
  Ava_Responsabilidades: "Responsabilidades",
  Ava_RelacoesInterpessoais: "Relações Interpessoais",
  Ava_AssiduidadePontualidade: "Assiduidade / Pontualidade",
  Ava_Testes: "Testes",
};

function _mediaDims(f) {
  const vals = _DIM_LABELS.map(([k]) => f[k]).filter((v) => v !== null && v !== undefined);
  if (!vals.length) return null;
  return (vals.reduce((a, b) => a + Number(b), 0) / vals.length).toFixed(2);
}

function DimSelect({ cursoId, fichaId, dim, value, onSaved }) {
  const [saving, setSaving] = useState(false);
  const onChange = async (e) => {
    const raw = e.target.value;
    setSaving(true);
    try {
      await adminApi.cursoAvaliacaoSet(cursoId, fichaId, {
        [dim]: raw === "" ? null : Number(raw),
      });
      await onSaved();
    } catch (err) {
      toast.error(err.message || "Erro a gravar");
    } finally { setSaving(false); }
  };
  return (
    <select
      className="w-11 border border-[#E6E1D6] rounded px-1 py-0.5 text-xs focus:border-[#A8843C] focus:outline-none disabled:opacity-50"
      value={value ?? ""}
      disabled={saving}
      onChange={onChange}
      title={_DIM_TITLES[dim]}
      data-testid={`ava-${fichaId}-${dim}`}
    >
      <option value="">—</option>
      <option value="1">1</option>
      <option value="2">2</option>
      <option value="3">3</option>
      <option value="4">4</option>
    </select>
  );
}

function ObservacaoInput({ cursoId, fichaId, current, onSaved }) {
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(current); }, [current]);
  const dirty = draft !== current;
  const save = async () => {
    setSaving(true);
    try {
      await adminApi.cursoAvaliacaoSet(cursoId, fichaId, { Ava_Observacoes: draft });
      await onSaved();
    } catch (e) {
      toast.error(e.message || "Erro a gravar observação");
    } finally { setSaving(false); }
  };
  return (
    <div className="flex gap-1">
      <input
        type="text"
        placeholder="—"
        className="flex-1 border border-[#E6E1D6] rounded px-2 py-1 text-xs focus:border-[#A8843C] focus:outline-none"
        value={draft || ""}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
        data-testid={`ava-obs-${fichaId}`}
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="px-2 py-1 rounded bg-[#A8843C] text-white text-xs hover:bg-[#8f6e2f] disabled:opacity-50"
          data-testid={`ava-obs-save-${fichaId}`}
        >{saving ? "…" : "OK"}</button>
      )}
    </div>
  );
}
